import classNames from 'classnames';
import React from 'react';

import {useFormatNumber, useI18n} from '@udemy/i18n';
import RatingStarIcon from '@udemy/icons/dist/rating-star.ud-icon';
import {BlockSkeleton} from '@udemy/react-reveal-components';
import {
    FALLBACK_COLLECTION_STATS,
    usePersonalPlanCollectionStats,
} from '@udemy/subscription-browse';

import {PersonalPlanLandingPageProps} from '../../personal-plan-landing-page-body.react-component';
import styles from './trusted-numbers.module.less';

export interface TrustedNumbersProps extends PersonalPlanLandingPageProps {
    className?: string;
}

export const TrustedNumbers = ({className, isPPLPRevamp}: TrustedNumbersProps) => {
    const {gettext} = useI18n();
    const {formatNumber} = useFormatNumber();

    const collectionStatsData = usePersonalPlanCollectionStats();

    // undefined means the hook resolved but has no collection data (e.g. active subscriber —
    // the backend does not return plan data for subscribers via the upgrade query).
    // In that case treat as non-loading and show fallback stats rather than a perpetual skeleton.
    const isCollectionStatsLoading = collectionStatsData?.isLoading ?? false;

    const contentCollectionStats =
        collectionStatsData && 'numberOfCourses' in collectionStatsData
            ? collectionStatsData
            : undefined;

    // TODO: Remove fallback stat logic once the backend is updated to return
    // the collection stats for active Personal Plan subscribers
    // https://udemy.atlassian.net/browse/CVR-2195
    const courses =
        contentCollectionStats?.numberOfCourses ?? FALLBACK_COLLECTION_STATS.numberOfCourses;
    const avgRating =
        contentCollectionStats?.courseAvgRating ?? FALLBACK_COLLECTION_STATS.courseAvgRating;
    const practiceExercises =
        contentCollectionStats?.numberOfPracticeExercises ??
        FALLBACK_COLLECTION_STATS.numberOfPracticeExercises;
    const instructors =
        contentCollectionStats?.numberOfInstructors ??
        FALLBACK_COLLECTION_STATS.numberOfInstructors;

    const loadingSkeleton = (
        <div data-testid="trusted-numbers-skeleton">
            <BlockSkeleton className={styles['loading-stat']} />
        </div>
    );

    const trustedNumberData = [
        {
            number: isCollectionStatsLoading ? loadingSkeleton : `${formatNumber(courses ?? 0)}+`,
            label: gettext('on-demand courses'),
        },
        {
            number: isCollectionStatsLoading
                ? loadingSkeleton
                : `${formatNumber(practiceExercises ?? 0)}+`,
            label: gettext('practice exercises'),
        },
        {
            number: isCollectionStatsLoading ? loadingSkeleton : formatNumber(avgRating ?? 0),
            label: gettext('average course rating'),
            icon: <RatingStarIcon className={styles['rating-star']} label={false} size="large" />,
        },
        {
            number: isCollectionStatsLoading
                ? loadingSkeleton
                : `${formatNumber(instructors ?? 0)}+`,
            label: gettext('top instructors'),
        },
    ];

    const featureHighlights = [
        {
            number: gettext('Career'),
            label: gettext('centered learning'),
            icon: undefined,
        },
        {
            number: gettext('AI skills'),
            label: gettext('& human ones too'),
            icon: undefined,
        },
        {
            number: gettext('Certification'),
            label: gettext('exam prep'),
            icon: undefined,
        },
        {
            number: gettext('AI-powered'),
            label: gettext('practice'),
            icon: undefined,
        },
    ];

    // Use different data array based on isPPLPRevamp prop
    const dataToRender = isPPLPRevamp ? featureHighlights : trustedNumberData;

    return (
        <dl
            className={classNames(className, styles['four-item-number-list'], {
                [styles.revamp]: isPPLPRevamp,
            })}
        >
            {dataToRender.map((item, index) => {
                return (
                    <div key={index}>
                        <dt className={classNames('ud-heading-xxl', styles['number'])}>
                            {item.number}
                            {item.icon}
                        </dt>
                        <dd className={classNames('ud-text-md', styles['label'])}>{item.label}</dd>
                    </div>
                );
            })}
        </dl>
    );
};
